<template>
  <ticket-header-base
    :ticket="ticketDetails"
    :actions="actionsList"
    @quick-fix="openQuickFixModal"
    @action-click="onActionClick($event, ticketDetails)"
  />
</template>

<script lang="ts">
import { defineComponent, toRefs, type PropType } from "vue";
import { useI18n } from "vue-i18n";

import TicketHeaderBase from "@/components/tickets/TicketHeaderBase.vue";
import TestEmailProxyModal from "@/components/modals/email-settings/TestEmailProxyModal.vue";

import { useDialogsStore } from "@/_store/dialogs.module";
import {
  type MspTicketDetails,
  useMspTicketsStore,
} from "@/_store/msp/tickets/msp-tickets.module.ts";
import { type TicketsScope } from "@/_store/roles.module";

import {
  type TicketPreviewHeaderAdapter,
  useTicketPreviewHeader,
} from "@/composables/tickets/useTicketPreviewHeader";

import { TicketAction, type TicketActionSeverity } from "@/constants/tickets";
import { SubscriptionAddon } from "@/constants/workplaces.ts";
import { ProxyTestType } from "@/constants/email-proxy.ts";
import { ModalWidth } from "@/constants/modals";
import { GTMParams } from "@/constants/gtm";

import { isMspActionRestricted } from "@/_helpers/msp-permissions.ts";
import { componentDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";
import { RolePermissionsScope } from "@/constants/roles.ts";

export default defineComponent({
  components: { TicketHeaderBase },

  props: {
    suspiciousVoteCount: {
      type: Number,
      default: 0,
    },
    safeVoteCount: {
      type: Number,
      default: 0,
    },
    ticketDetails: {
      type: Object as PropType<MspTicketDetails>,
      required: true,
    },
  },

  setup(props) {
    const { ticketDetails } = toRefs(props);

    const i18n = useI18n();
    const dialogsStore = useDialogsStore();
    const ticketsStore = useMspTicketsStore();

    const getDisablePolicy = (
      ticket: MspTicketDetails,
      nestedPermissionKey: TicketActionSeverity
    ): boolean =>
      isMspActionRestricted(
        RolePermissionsScope.TICKETS,
        ticket.moduleName as keyof TicketsScope,
        nestedPermissionKey
      );

    const onTestMxRecord = (item: MspTicketDetails, _severity: TicketActionSeverity) => {
      dialogsStore.openDialog({
        ...componentDialogsConfigConstructor({
          item: {
            ...item,
            testType: ProxyTestType.INBOUND_CONNECTION,
          },
          action: TicketAction.TEST_MX_RECORD,
          title: i18n.t(`modals.testDomain.${ProxyTestType.INBOUND_CONNECTION}.title`),
          component: TestEmailProxyModal,
          width: ModalWidth.MEDIUM,
          callback: () => {},
          disable: isMspActionRestricted(
            RolePermissionsScope.PROTECTION,
            SubscriptionAddon.INBOUND_GATEWAY
          ),
        }),
        footer: null,
      });
    };

    // Adapter encapsulates MSP-specific behavior for the shared header composable.
    const adapter: TicketPreviewHeaderAdapter<MspTicketDetails> = {
      applyAction: ticketsStore.applyMspTicketAction,
      applyDownloadAction: ticketsStore.applyMspTicketDownloadEmailAction,
      applyQuickFixAction: ticketsStore.applyQuickFixAction,

      getRecipients: (eventId: string, isDevice: boolean, workspaceId?: string) =>
        ticketsStore.getTicketRecipients(
          eventId,
          isDevice,
          workspaceId ?? props.ticketDetails.workspaceId
        ),

      isActionRestricted: (ticket: MspTicketDetails, severity: TicketActionSeverity) =>
        getDisablePolicy(ticket, severity),

      onContactUser: (payload) =>
        ticketsStore.onContactUser({
          action: payload.action,
          metadata: payload.metadata,
          item: {
            ...payload.item,
            workspaceName: payload.item.workspaceName ?? props.ticketDetails.workspaceName,
          },
        }),

      checkProxyConfig: () => false,
      gtmContext: GTMParams.MSP,
      getDisclaimer: (disable: boolean) => (disable ? i18n.t("general.noPermissions") : undefined),
      onTestMxRecord,
    };

    const { actionsList, onActionClick, openQuickFixModal } = useTicketPreviewHeader(
      ticketDetails,
      adapter
    );

    return {
      actionsList,
      onActionClick,
      openQuickFixModal,
    };
  },
});
</script>
